import { useCallback, useState } from "react";

import { ProjectEntryMethod } from "../../../../../models";

interface UseEntryMethodSwitchParams {
  currentMethod: ProjectEntryMethod;
  /** Whether switching away from the current method would throw entered data away. */
  hasEnteredData: () => boolean;
  /** Performs the switch, once it is either safe or confirmed. */
  onSwitch: (method: ProjectEntryMethod) => void;
}

export interface UseEntryMethodSwitchReturnData {
  pendingMethod: ProjectEntryMethod | null;
  /** Bump this as the toggle's key to snap its selection back after a cancelled switch. */
  toggleResetKey: number;
  requestSwitch: (method: ProjectEntryMethod) => void;
  confirmSwitch: () => void;
  keepEntries: () => void;
}

/**
 * The manual/upload toggle's guard: a switch that would discard entered rows is held as pending
 * until the SwitchingDataEntryWarningModal confirms or cancels it; one that discards nothing goes
 * straight through.
 */
export const useEntryMethodSwitch = ({
  currentMethod,
  hasEnteredData,
  onSwitch,
}: UseEntryMethodSwitchParams): UseEntryMethodSwitchReturnData => {
  const [pendingMethod, setPendingMethod] = useState<ProjectEntryMethod | null>(null);
  const [toggleResetKey, setToggleResetKey] = useState(0);

  const requestSwitch = useCallback(
    (method: ProjectEntryMethod) => {
      if (method === currentMethod) return;

      if (hasEnteredData()) {
        setPendingMethod(method);
        return;
      }

      onSwitch(method);
    },
    [currentMethod, hasEnteredData, onSwitch]
  );

  const confirmSwitch = useCallback(() => {
    if (!pendingMethod) return;

    onSwitch(pendingMethod);
    setPendingMethod(null);
  }, [onSwitch, pendingMethod]);

  const keepEntries = useCallback(() => {
    setPendingMethod(null);
    // RadioButtonGroup moves its selection optimistically and only re-syncs when the value prop
    // changes - which it does not when a switch is cancelled. Remounting puts the dot back.
    setToggleResetKey((previous) => previous + 1);
  }, []);

  return { pendingMethod, toggleResetKey, requestSwitch, confirmSwitch, keepEntries };
};
